<template>
  <input-base
    :input="input"
    :review-mode="reviewMode"
    :error="error"
  >
    <div
      v-if="(!input.attributes.locked && !reviewMode)"
      v-show="canUploadFiles"
      class="w-full"
      :class="{
        'border-b pb-2': applicationValue.length
      }"
    >
      <app-button
        tag="label"
        class="mt-1"
        :disabled="loading"
        style="max-width:12rem"
        :for="input.key"
      >
        <icon
          icon="collection"
          class="w-4 h-5 icons-knockout mr-2"
        />
        <span v-if="!loading">Upload {{ singularFile }}</span>
        <span v-else>Uploading...</span>
      </app-button>

      <icon-link
        v-if="input.attributes.template"
        :href="downloadRoute(input.attributes.template, false)"
        icon="arrow-thin-down-circle"
        class="inline-block ml-8"
        target="_blank"
      >
        Download template
      </icon-link>

      <input
        :id="input.key"
        ref="files"
        class="w-full hidden"
        type="file"
        :name="`${input.key}[]`"
        :multiple="input.attributes.multiple"
        :placeholder="input.attributes.placeholder"
        :accept="input.validation.accept.join(',')"
        :disabled="loading || !canUploadFiles"
        @change="addFiles"
        v-on="$listeners"
      >
    </div>
    <div
      class="flex-1"
      :class="{
        'mr-2': reviewMode
      }"
    >
      <ul class="list-reset pt-2">
        <li
          v-for="(file, index) in failedFiles"
          :key="'failed_files_' + index"
          class="border p-2 border-dashed border-negative-700 text-negative-900 bg-negative-100"
        >
          {{ file.name }} [{{ Math.round((file.size / 1000 / 1000) * 100) / 100 }}mb]
          <small
            v-if="file.errors"
            class="block pt-1 italic"
          >{{ file.errors.join(', ') }}</small>
        </li>
        <li
          v-for="(file, index) in tempFiles"
          :key="'uploading_files_' + index"
          class="border p-2 border-dashed border-grey-700 text-grey-500 bg-grey-100"
        >
          {{ file.name }} [{{ Math.round((file.size / 1000 / 1000) * 100) / 100 }}mb]
          <small class="block pt-1 italic">Uploading...</small>
        </li>
        <li
          v-for="(file, index) in applicationValue"
          :key="'uploaded_files_' + index"
          class="flex items-center border p-2 border-dashed bg-grey-100"
        >
          <button
            v-if="!reviewMode"
            type="button"
            class="p-0 mr-2 cursor-pointer"
            @click="removeFile(input.key, index, file.id)"
          >
            <icon
              icon="close-circle"
              class="w-5 h-5 icons-negative block m-0"
            />
          </button>
          <span class="flex-1">{{ file.name }} <small>[{{ file.size }}]</small></span>
          <icon-link
            :href="downloadRoute(file.id)"
            target="_blank"
            icon="cheveron-down-circle"
            icon-placement="right"
          >
            Download
          </icon-link>
        </li>
      </ul>
    </div>
  </input-base>
</template>

<script>
import { inputHelperMixin } from '../../mixins/inputHelper';
export default {
	name: 'InputUpload',
	mixins: [inputHelperMixin],
	data() {
		return {
			loading: false,
			tempFiles: [],
			failedFiles: []
		}
	},
	computed: {
		canUploadFiles() {
      if(this.reviewMode) return false;
			if(!this.input.attributes.multiple) return this.applicationValue.length < 1;
			return this.applicationValue.length < this.input.validation.max;
		},
    singularFile() {
      return this.input.attributes.multiple ? 'files' : 'file';
    }
	},
	methods: {
    downloadRoute(id, project = true) {
      let append = project ? '?project=1' : '';
      return route('applications.files.download', {
          application: window._project,
          file: id
        }) + append
    },
		removeFile(fieldKey, index, id) {
      if(this.reviewMode) return;
      const removeReference = () => {
				const fileList = [...this.applicationValue].filter((file, i) => i !== index);
				return this.updateInput(this.parentField, fieldKey, fileList, null, true)
      }
			this.failedFiles = [];
			axios.delete(route( 'applications.files.delete', {
          application: window._project,
          file: id
        }))
        .then(removeReference)
        .catch(error => {
          if (error.response && error.response.status === 404) {
            // File not found, just remove reference
            return removeReference();
          }
          // eslint-disable-next-line no-console
          console.log(error)
        });
		},
		validFile(file) {
			const { validation } = this.input;
			const errors = [];
			if(validation.limit) {
				if((file.size / 1024 / 1024) > validation.limit) errors.push(`This file exceeds the upload limit of ${validation.limit}mb`)
			}
			if((this.tempFiles.length + this.applicationValue.length) === validation.max) errors.push(`You can only upload a maximum of ${ validation.max } files.`)
			if(errors.length) {
				file['errors'] = errors
			}
			return file
		},
		addFiles() {
      if(this.reviewMode) return;
			this.tempFiles = [];
			this.failedFiles = [];
			this.loading = true;
      this.uploadValue = 0;

			const inputFiles = this.$refs.files.files;
			let formData = new FormData();

			formData.append('parent_key', this.parentField)
			formData.append('key', this.input.key)

			for( let i = 0; i < inputFiles.length; i++ ) {
				let file = this.validFile(inputFiles[i])
				if(file.hasOwnProperty('errors')) {
					this.failedFiles.push(file)
				} else {
          formData.append(`${this.input.key}[${i}]`, file)
					this.tempFiles.push(file);
				}
			}

			axios.post(route('applications.upload', window._project), formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			})
			.then(({data}) => {
				this.updateInput(this.parentField, this.input.key, [...this.applicationValue, ...data], null, true)
			})
			.catch(({response, data}) => {
				const fileList = this.tempFiles.map(file => {
					file['errors'] = [`Unabled to upload (${response.status}: ${response.statusText})`]
					return file;
				});
				this.failedFiles = [...fileList, ...this.failedFiles]
			}).then(() => {
        formData = null
				this.$refs.files.setAttribute('files', null)
				this.$refs.files.setAttribute('value', '')
				this.loading = false
				this.tempFiles = []
			})


		}
	}
}
</script>
